import { el, clear } from '../ui/dom';
import {
  setBootstrapToken,
  getLastEmail,
  rememberEmail,
  prefersPasskey,
} from '../auth';
import {
  signInWithPasskey,
  signInWithPasskeyAutofill,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
} from '../api/auth';
import { navigate } from '../router';
import type { RouteContext } from '../router';

export function renderLogin({ root }: RouteContext): void {
  clear(root);

  const supportsPasskeys = browserSupportsWebAuthn();
  const error = el('div', { class: 'banner banner-error', hidden: true });

  // ── Passkey sign-in (primary) ──────────────────────────────────────
  const email = el('input', {
    class: 'control',
    type: 'email',
    // "webauthn" lets the browser surface saved passkeys in this field's autofill.
    autocomplete: 'username webauthn',
    placeholder: 'Email (optional)',
  });
  const lastEmail = getLastEmail();
  if (lastEmail) email.value = lastEmail;

  // Plain, no-jargon label — and lead with the known user when we have one.
  const primaryLabel =
    prefersPasskey() && lastEmail ? `Continue as ${lastEmail}` : 'Sign in with your device';
  const passkeyBtn = el(
    'button',
    { class: 'btn btn-primary', type: 'button', disabled: !supportsPasskeys },
    [primaryLabel],
  );

  function showError(message: string) {
    error.textContent = message;
    error.hidden = false;
  }

  passkeyBtn.addEventListener('click', async () => {
    error.hidden = true;
    passkeyBtn.disabled = true;
    passkeyBtn.textContent = 'Waiting for your device…';
    try {
      const typed = email.value.trim();
      if (typed) rememberEmail(typed);
      await signInWithPasskey(typed || undefined);
      navigate('/');
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') {
        showError('Sign-in cancelled, or no passkey was found for this device.');
      } else {
        showError(err instanceof Error ? err.message : 'Sign-in failed.');
      }
      passkeyBtn.disabled = false;
      passkeyBtn.textContent = primaryLabel;
    }
  });

  // Automatic: passively offer saved passkeys in the email field's autofill.
  // Resolves only if the user picks one; a later modal ceremony cancels it.
  void (async () => {
    if (!supportsPasskeys || !(await browserSupportsWebAuthnAutofill())) return;
    try {
      await signInWithPasskeyAutofill();
      navigate('/');
    } catch {
      /* dismissed / no passkey chosen — ignore */
    }
  })();

  const passkeySection = el('div', { class: 'login-section' }, [
    email,
    passkeyBtn,
    !supportsPasskeys
      ? el('p', { class: 'login-note' }, [
          'This browser does not support passkeys. Use first-time setup below.',
        ])
      : null,
  ]);

  // ── First-time setup: bootstrap token (secondary, collapsed) ───────
  const tokenInput = el('textarea', {
    class: 'token-input',
    placeholder: 'Paste an admin access token (JWT)…',
    rows: 4,
  });
  const tokenForm = el('form', { class: 'login-section' }, [
    el('p', { class: 'login-note' }, [
      'Enrolling a passkey requires signing in once. Paste a valid access ' +
        'token from an account with the ADMIN role, then add a passkey from ' +
        'the Security screen.',
    ]),
    tokenInput,
    el('button', { class: 'btn btn-ghost', type: 'submit' }, ['Continue']),
  ]);
  tokenForm.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const value = tokenInput.value.trim();
    if (!value) {
      showError('Enter an access token to continue.');
      return;
    }
    setBootstrapToken(value);
    navigate('/');
  });

  const card = el('div', { class: 'login-card' }, [
    el('h1', { class: 'login-title' }, ['Teams Monitoring']),
    el('p', { class: 'login-sub' }, ['Check ID Cloud — internal admin']),
    error,
    passkeySection,
    el('details', { class: 'login-details' }, [
      el('summary', {}, ['First-time setup']),
      tokenForm,
    ]),
  ]);

  root.append(el('div', { class: 'login-wrap' }, [card]));
}
